import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { Button } from "primereact/button";
import { T } from "services/lang/LanguageService";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { SelectButton } from "primereact/selectbutton";
import { Tree } from "primereact/tree";
import React, { BaseSyntheticEvent, useState } from "react";
import DatasetTableDatasetEditor from "./dataset-table-editor/dataset-table-dataset-editor/DatasetTableDatasetEditor";

interface IGroupedHeadersDatasetSelectorProps {
  currentConfig: any;
  setCurrentConfig: (value: any) => void;
}

interface ISelectedNode {
  key: string;
  label: string;
  children?: ISelectedNode[];
}

const GroupedHeadersDatasetSelector = ({
  currentConfig,
  setCurrentConfig,
}: IGroupedHeadersDatasetSelectorProps) => {
  const [selectedNode, setSelectedNode] = useState<ISelectedNode>();
  const [newNodeLabel, setNewNodeLabel] = useState("");
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [nodeType, setNodeType] = useState("node");
  const [selectedDatasetNode, setSelectedDatasetNode] = useState<ISelectedNode>();

  const addNode = (event: BaseSyntheticEvent): void => {
    event.preventDefault();
    const newCurrentConfig = currentConfig;
    const newNode =
      nodeType === "node" ? { header: newNodeLabel, children: [] } : { identifier: newNodeLabel };

    if (selectedNode?.key) {
      const indices = selectedNode.key.split("-").slice(1).map(Number);
      let parentNode = currentConfig.datasets[indices[0]];
      for (let i = 1; i < indices.length; i++) {
        parentNode = parentNode.children[indices[i]];
      }

      parentNode.children = parentNode.children ? [...parentNode.children, newNode] : [newNode];
    } else {
      newCurrentConfig.datasets = [...currentConfig.datasets, newNode];
    }
    setCurrentConfig({ ...newCurrentConfig });
    setNewNodeLabel("");
    setShowAddDialog(false);
  };

  const deleteNode = () => {
    if (selectedNode) {
      const indices = selectedNode.key.split("-").slice(1).map(Number);

      if (indices.length === 1) {
        currentConfig.datasets.splice(indices[0], 1);
      } else {
        let parentNode = currentConfig.datasets[indices[0]];
        for (let i = 1; i < indices.length - 1; i++) {
          parentNode = parentNode.children[indices[i]];
        }
        parentNode.children.splice(indices[indices.length - 1], 1);
      }
      setCurrentConfig({ ...currentConfig });
      setSelectedNode(undefined);
      setShowDeleteDialog(false);
    }
  };

  const handleDragDrop = (event: any) => {
    const { dragNode, dropNode, dropIndex } = event;
    const dragIndices = dragNode.key.split("-").slice(1).map(Number);
    const dropIndices = dropNode.key.split("-").slice(1).map(Number);
    const newDatasets = JSON.parse(JSON.stringify(currentConfig.datasets));

    let movedNodeParent = newDatasets;
    for (let i = 0; i < dragIndices.length - 1; i++) {
      movedNodeParent = movedNodeParent[dragIndices[i]].children;
    }
    const movedNode = movedNodeParent.splice(dragIndices[dragIndices.length - 1], 1)[0];

    let dropNodeParent = newDatasets;
    for (let i = 0; i < dropIndices.length; i++) {
      dropNodeParent = dropNodeParent[dropIndices[i]].children;
    }
    dropNodeParent.splice(dropIndex, 0, movedNode);
    setCurrentConfig({ ...currentConfig, datasets: newDatasets });
  };

  const convertToTreeFormat = (data: any, parentKey = "") => {
    return data.map((item: any, index: number) => {
      const key = `${parentKey}-${index}`;
      const node = {
        key,
        label: item?.header || item?.identifier,
        children: item?.children ? convertToTreeFormat(item.children, key) : null,
      };
      return node;
    });
  };

  const updateNodeInConfig = (nodeKey: string, newIdentifier: string) => {
    const indices = nodeKey.split("-").slice(1).map(Number);
    const newDatasets = JSON.parse(JSON.stringify(currentConfig.datasets));

    const updateNode = (node: any, ind: number[], level: number) => {
      const updatedNode = node;
      if (level === ind.length) {
        if (updatedNode.header) {
          updatedNode.header = newIdentifier;
        } else if (updatedNode.identifier) {
          updatedNode.identifier = newIdentifier;
        }
        return;
      }

      updateNode(node.children[indices[level]], indices, level + 1);
    };

    updateNode(newDatasets[indices[0]], indices, 1);
    setCurrentConfig({ ...currentConfig, datasets: newDatasets });
  };

  const handleNodeUpdate = (event: BaseSyntheticEvent): void => {
    event.preventDefault();
    updateNodeInConfig(selectedNode?.key ?? "", selectedNode?.label ?? "");
    setSelectedNode(undefined);
    setShowUpdateDialog(false);
  };

  const handleDatasetUpdate = (newDataset: any) => {
    const identifierToFind = newDataset.identifier;
    const newConfig = currentConfig.datasets;

    const findNode = (node: any, identifier: string) => {
      if (node.identifier && node.identifier === identifier) {
        setSelectedDatasetNode(newDataset);
        return newDataset;
      }
      if (node.children) {
        for (let i = 0; i < node.children.length; i++) {
          const updatedNode = findNode(node.children[i], identifier);
          if (updatedNode) {
            const currentNode = node;
            currentNode.children[i] = updatedNode;
            if (currentNode.children[i].identifier) {
              setSelectedDatasetNode(updatedNode);
            }
            return currentNode;
          }
        }
      }
      return null;
    };
    for (let i = 0; i < newConfig.length; i++) {
      const updatedNode = findNode(newConfig[i], identifierToFind);
      if (updatedNode) {
        newConfig[i] = updatedNode;
        break;
      }
    }
    setCurrentConfig({ ...currentConfig, datasets: newConfig });
  };

  const treeData = convertToTreeFormat(currentConfig.datasets);

  const findNodeByIdentifier = (identifierToFind: string, data: any) => {
    let foundNode: any = {};
    const findNode = (node: any) => {
      if (node.identifier) {
        if (node.identifier === identifierToFind) {
          return (foundNode = node);
        }
      }
      if (node.children) {
        for (const child of node.children) {
          findNode(child);
          if (child.identifier === identifierToFind) {
            return (foundNode = child);
          }
        }
      }
      return null;
    };

    for (const item of data) {
      findNode(item);
    }
    setSelectedDatasetNode(foundNode);
    return null;
  };

  const editSelectedNode = (e: any, nodeLabel: string) => {
    if (nodeLabel === "nodeLabel") {
      setSelectedNode(prevState => {
        return {
          ...prevState,
          label: e.target.value,
        } as ISelectedNode;
      });
    }
    if (nodeLabel === "ddNodeLabel") {
      setSelectedNode(prevState => {
        return {
          ...prevState,
          label: e as string,
        } as ISelectedNode;
      });
    }
  };

  const nodeTemplate = (node: any) => {
    return (
      <div className="flex w-full align-items-center justify-content-between p-0">
        {node.children ? <span>{node.label}</span> : <DatasetTitle identifier={node.label} />}
        <div>
          {node.children ? (
            <>
              <Button
                type="button"
                icon="pi pi-plus"
                tooltip={T("action.add-group-or-dataset")}
                className="p-button-rounded p-button-success p-button-text p-ml-2"
                style={{ fontSize: "0.8rem" }}
                onClick={() => {
                  setSelectedNode(node);
                  setShowAddDialog(true);
                }}
              />
              <Button
                type="button"
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text p-ml-2"
                tooltip={T("action.change-header")}
                style={{ fontSize: "0.8rem" }}
                onClick={() => {
                  setSelectedNode(node);
                  setShowUpdateDialog(true);
                }}
              />
            </>
          ) : (
            <>
              <Button
                type="button"
                icon="pi pi-refresh"
                tooltip={T("action.change-dataset")}
                className="p-button-rounded p-button-success p-button-text p-ml-2"
                style={{ fontSize: "0.8rem" }}
                onClick={() => {
                  setSelectedNode(node);
                  setShowUpdateDialog(true);
                }}
              />
              <Button
                type="button"
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text p-ml-2"
                tooltip={T("action.edit-dataset")}
                style={{ fontSize: "0.8rem" }}
                onClick={() => {
                  setSelectedNode(node);
                  setShowEditDialog(true);
                  findNodeByIdentifier(node.label, currentConfig.datasets);
                }}
              />
            </>
          )}
          <Button
            type="button"
            icon="pi pi-trash"
            className="p-button-rounded p-button-danger p-button-text p-ml-2"
            tooltip={T("action.remove")}
            style={{ fontSize: "0.8rem" }}
            onClick={() => {
              setSelectedNode(node);
              setShowDeleteDialog(true);
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="w-full flex flex-column">
      <Button
        type="button"
        label={T("word.add")}
        tooltip={T("action.add-group-or-dataset")}
        className="p-button-success p-button-text align-self-end max-w-max p-mb-2"
        style={{ fontSize: "0.8rem" }}
        onClick={() => {
          setSelectedNode(undefined);
          setShowAddDialog(true);
        }}
      />
      <Dialog
        header={T("action.update")}
        visible={showUpdateDialog}
        onHide={() => setShowUpdateDialog(false)}
        style={{ width: "400px" }}
      >
        <div className="flex flex-column gap-2">
          {selectedNode?.children ? (
            <InputText
              id="nodeLabel"
              className="w-full"
              value={selectedNode?.label || ""}
              onChange={e => editSelectedNode(e, "nodeLabel")}
            />
          ) : (
            <DatasetDropdown
              value={selectedNode?.label || ""}
              onChange={e => editSelectedNode(e, "ddNodeLabel")}
            />
          )}
          <Button
            className="align-self-center p-button-success"
            label={T("action.update")}
            onClick={handleNodeUpdate}
          />
        </div>
      </Dialog>
      <Dialog
        header={T("word.add")}
        visible={showAddDialog}
        onHide={() => setShowAddDialog(false)}
        style={{ width: "400px" }}
      >
        <div className="flex flex-column gap-1">
          <SelectButton
            value={nodeType}
            options={[
              { label: T("word.group"), value: "node" },
              { label: T("word.dataset"), value: "dataset" },
            ]}
            onChange={e => setNodeType(e.value)}
            className="mb-2"
          />
          {nodeType === "node" ? (
            <InputText
              id="newNodeLabel"
              className="w-full"
              value={newNodeLabel}
              placeholder={T("word.enter-group-name")}
              onChange={e => setNewNodeLabel(e.target.value)}
            />
          ) : (
            <DatasetDropdown value={newNodeLabel} onChange={e => setNewNodeLabel(e as string)} />
          )}
          <Button className="align-self-center p-button-success" label={T("word.add")} onClick={addNode} />
        </div>
      </Dialog>

      <Dialog
        header={T("action.edit")}
        visible={showEditDialog}
        onHide={() => setShowEditDialog(false)}
        style={{ width: "90%" }}
        modal={false}
      >
        <div className="col-12 gap-2 flex flex-column justify-content-center align-items-center">
          <DatasetTableDatasetEditor
            currentConfig={selectedDatasetNode}
            setCurrentConfig={handleDatasetUpdate}
            onConfigChange={() => {}}
          />
        </div>
      </Dialog>

      <ConfirmDialog
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        message="Are you sure you want to delete?"
        header={T("action.confirm-delete")}
        icon="pi pi-exclamation-triangle"
        accept={deleteNode}
        reject={() => setShowDeleteDialog(false)}
        acceptClassName="bg-red-500 border-red-500"
      />
      {treeData.length > 0 ? (
        <Tree
          className="w-full p-0"
          value={treeData}
          dragdropScope="groupedHeaders"
          onDragDrop={handleDragDrop}
          nodeTemplate={nodeTemplate}
        />
      ) : (
        <p className="text-center font-bold w-full border-1 border-black-alpha-30 p-2">
          {T("message.no-group-or-dataset-added")}
        </p>
      )}
    </div>
  );
};

export default GroupedHeadersDatasetSelector;
